import { ITEM_TYPES } from '@constants/itemTypes';
import { batchStateSchema } from '@state/_shared/batchStateSchema';
import { batchDetailsDialogStateSchema } from '@state/_shared/batchDetailsDialogStateSchema';
import { createPageTableSchema } from '@state/_shared/tableComponentStateSchema';
import {
    BATCH_CAPTURE_STATUSES,
    BATCH_MODE_ITEM_TYPES,
    BATCH_MODE_PHASES
} from '@pages/Catalog/constants/batchMode';

// Define schema for lot rows (used in itemActionComponent)
const lotRowSchema = {
    type: 'object',
    properties: {
        // Core identification fields
        id: { type: ['string', 'number'] },
        lot_id: { type: ['string', 'number'] },
        color_id: { type: ['string', 'number'] },
        item_number: { type: 'string' },
        item_type: { type: 'string' },
        // Stamped on by useItemService.transformItemResponse and
        // useLotService.normalizeLotItemIdentity. AddToBatchButton requires it.
        item_name: { type: ['string', 'null'] },
        
        // Display fields
        color_name: { type: ['string', 'null'] },
        color_html: { type: ['string', 'null'] },
        image_url: { type: ['string', 'null'] },
        
        // Pricing fields (US)
        used_avg_sold_price_us: { type: ['number', 'null'] },
        used_avg_inventory_price_us: { type: ['number', 'null'] },
        used_low_price_us: { type: ['number', 'null'] },
        used_sell_through_us: { type: ['number', 'null'] },
        new_avg_sold_price_us: { type: ['number', 'null'] },
        new_avg_inventory_price_us: { type: ['number', 'null'] },
        new_low_price_us: { type: ['number', 'null'] },
        new_sell_through_us: { type: ['number', 'null'] },
        
        // Pricing fields (Global)
        used_avg_sold_price_global: { type: ['number', 'null'] },
        used_avg_inventory_price_global: { type: ['number', 'null'] },
        used_low_price_global: { type: ['number', 'null'] },
        used_sell_through_global: { type: ['number', 'null'] },
        new_avg_sold_price_global: { type: ['number', 'null'] },
        new_avg_inventory_price_global: { type: ['number', 'null'] },
        new_low_price_global: { type: ['number', 'null'] },
        new_sell_through_global: { type: ['number', 'null'] },
        
        // Reference price (computed field)
        reference_price: { type: ['number', 'null'] },

        // Sales-history display and acceptance fields
        last_sold_date: { type: ['string', 'null'] },
        sell_through: { type: ['number', 'null'] },

        // Timestamp fields
        bricklinkSyncedAt: { type: ['string', 'null'] },
        createdAt: { type: ['string', 'null'] },
        updatedAt: { type: ['string', 'null'] },
        
        // Search and utility fields
        composite_ssearch: { type: ['string', 'null'] }
    }
};

// Define actions specific to lot rows (if any, otherwise empty object)
const lotRowActions = {
    // Add actions like isSelecting, etc. if needed
};

// Create the table schema for the item action component
const itemActionTableSchema = createPageTableSchema(lotRowSchema, lotRowActions);

// Schema definition for catalog page state
// This file defines the structure and types of the catalog page state

export const catalogPageStateSchema = {
    // --- Batch State Integration ---
    batches: batchStateSchema.batches, // Keep using shared definition for the list
    isLoading: {
        type: 'boolean',
        default: false,
        description: 'Indicates if catalog data is currently loading.'
    },
    
    // Batch selector component state
    batchSelector: {
        type: 'object',
        properties: {
            isLoading: {
                type: 'boolean',
                default: false,
                description: 'Indicates if batch selector is loading batch data.'
            }
        }
    },

    selectedBatchId: {
        type: 'string',
        default: null,
        persist: true
    },

    // Keep page-specific batchId for item processing context
    // Note: batchId in itemProcessing might need syncing with selectedBatchId
    itemProcessing: {
        type: 'object',
        properties: {
            searchType: {
                type: 'string',
                enum: ['image', 'number', 'name'],
                default: null
            },
            itemNumber: {
                type: 'string',
                default: null
            },
            itemName: {
                type: 'string',
                default: null
            },
            itemType: {
                type: 'string',
                enum: ITEM_TYPES.filter(t => t !== 'UNSORTED_LOT'),
                default: null
            },
            colorId: {
                type: 'number',
                default: null
            },
            lotId: {
                type: 'number',
                default: null
            },
            colorName: {
                type: 'string',
                default: null
            },
            colorHtml: {
                type: 'string',
                default: null
            },
            price: {
                type: 'number',
                default: null
            },
            quantity: {
                type: 'number',
                default: null
            },
            userInventoryLocationId: {
                type: 'string',
                default: null
            },
            userInventoryLocationName: {
                type: 'string',
                default: null
            },
            condition: {
                type: 'string',
                enum: ['N', 'U'],
                default: 'U',
                persist: true
            },
            partDestinations: {
                type: 'array',
                default: [],
                itemSchema: {
                    type: 'object',
                    properties: {
                        user_inventory_item_location_id: { type: 'string' },
                        user_inventory_item_location_name: { type: 'string' },
                        qty_on_hand: { type: 'number' },
                        recommended: { type: 'boolean' },
                        name: { type: 'string' }
                    }
                }
            },
            setDestinations: {
                type: 'array',
                default: [],
                itemSchema: {
                    type: 'object',
                    properties: {
                        number: { type: 'string' },
                        name: { type: 'string' },
                        user_set_item_id: { type: 'number' },
                        qty_have: { type: 'number' },
                        qty_to_allocate: { type: 'number' },
                        user_inventory_item_location_id: { type: ['number', 'null'] },
                        user_inventory_item_location_name: { type: ['string', 'null'] },
                        part_out_value: { type: 'number' },
                        piece_count: { type: 'number' }
                    }
                }
            },
            isAddingToBatch: {
                type: 'boolean',
                default: false
            },
            // Available lots for the currently selected item
            lots: {
                type: 'array',
                default: [],
                description: 'Available lots data for the currently selected item',
                itemSchema: lotRowSchema
            },
            lotsCacheId: {
                type: ['string', 'null'],
                default: null,
                description: 'Item, type, and condition identity for the currently loaded lots.'
            },
            autoRefreshedLotId: {
                type: ['number', 'null'],
                default: null,
                description: 'Lot automatically refreshed from BrickLink for the current single-lot selection.'
            }
        }
    },
    findPartCard: {
        type: 'object',
        properties: {
            isEnabled: {
                type: 'boolean',
                default: false
            },
            webcamCaptureSearchSection: {
                type: 'object',
                properties: {
                    isOpen: {
                        type: 'boolean',
                        default: false
                    },
                    itemLookupType: {
                        type: 'string',
                        enum: ['PART', 'MINIFIG', 'SET', 'ANY'],
                        persist: true,
                        default: 'PART'
                    },
                    webcamPreview: {
                        type: 'object',
                        properties: {
                            isOpen: {
                                type: 'boolean',
                                default: false
                            }
                        }
                    },
                    webcamState: {
                        type: 'object',
                        properties: {
                            stayActive: {
                                type: 'boolean',
                                persist: true,
                                default: true
                            },
                            isOptionsOpen: {
                                type: 'boolean',
                                default: false
                            },
                            showWebcam: {
                                type: 'boolean',
                                default: false
                            },
                            videoDevices: {
                                type: 'array',
                                default: []
                            },
                            selectedDevice: {
                                type: 'string',
                                persist: true,
                                default: ''
                            },
                            stream: {
                                type: 'null',
                                default: null
                            },
                            isScanning: {
                                type: 'boolean',
                                default: false
                            },
                            isWebcamActive: {
                                type: 'boolean',
                                default: false
                            },
                            isInputFocused: {
                                type: 'boolean',
                                default: false
                            }
                        }
                    }
                }
            },
            numberSearchSection: {
                type: 'object',
                properties: {
                    isOpen: {
                        type: 'boolean',
                        default: false
                    },
                    isLoading: {
                        type: 'boolean',
                        default: false
                    },
                    itemType: {
                        type: 'string',
                        enum: ITEM_TYPES.filter(t => t !== 'UNSORTED_LOT'),
                        persist: true,
                        default: 'PART'
                    },
                    numberInput: {
                        type: 'string',
                        default: ''
                    }
                }
            },
            nameSearchSection: {
                type: 'object',
                properties: {
                    isOpen: {
                        type: 'boolean',
                        default: false
                    },
                    isInputFocused: {
                        type: 'boolean',
                        default: false
                    },
                    itemType: {
                        type: 'string',
                        enum: ITEM_TYPES.filter(t => t !== 'UNSORTED_LOT'),
                        default: 'PART'
                    },
                    partNameSearch: {
                        type: 'object',
                        properties: {
                            nameInput: {
                                type: 'string',
                                default: ''
                            },
                            isLoading: {
                                type: 'boolean',
                                default: false
                            }
                        }
                    },
                    optionsSection: {
                        type: 'object',
                        properties: {
                            isOpen: {
                                type: 'boolean',
                                default: false
                            }
                        }
                    }
                }
            }
        }
    },
    idPartCard: {
        type: 'object',
        properties: {
            isEnabled: {
                type: 'boolean',
                default: false
            },
            identifiedItems: {
                type: 'object',
                properties: {
                    isLoading: {
                        type: 'boolean',
                        default: false
                    },
                    loadingItemKey: {
                        type: ['string', 'null'],
                        default: null,
                        description: 'Currently loading identified item key (number::TYPE) for showing in-list spinners.'
                    },
                    items: {
                        type: 'array',
                        description: 'List of identified items from search results.',
                        default: [],
                        itemSchema: {
                            type: 'object',
                            properties: {
                                id: { type: 'string', description: 'Original ID from source (e.g., Brickognize)' },
                                item_number: { type: 'string', description: 'Item number (e.g., BrickLink ID)' },
                                item_name: { type: 'string', description: 'Item name' },
                                item_id: { type: 'number', description: 'Parsed item ID (numeric)' },
                                item_type: { type: 'string', description: 'Type of the item (e.g., part, minifig)' },
                                image_url: { type: 'string', description: 'URL for the item image' },
                                score: { type: 'number', description: 'Confidence score from recognition service' },
                                category: { type: 'string', description: 'Item category' },
                                external_sites: { 
                                    type: 'array', 
                                    description: 'Links to external sites like BrickLink',
                                    default: [],
                                    itemSchema: { type: 'object' }
                                },
                                lookalikes: {
                                    type: 'array',
                                    description: 'Linked lookalike items for this item',
                                    default: [],
                                    itemSchema: { type: 'object' }
                                },
                                lots: { 
                                    type: 'array', 
                                    description: 'Color/lot information for the item',
                                    default: [],
                                    itemSchema: { type: 'object' }
                                }
                            }
                        }
                    }
                }
            },
            selectedItem: {
                type: ['object', 'null'],
                default: null,
                properties: {
                    id: { type: 'number' },
                    number: { type: 'string' },  // Standard API field
                    name: { type: 'string' },
                    type: { type: 'string' },
                    item_number: { type: 'string' },  // Legacy UI field
                    item_id: { type: 'number' },
                    img_url: { type: 'string' },
                    category: { type: 'string' },
                    weight: { type: 'number' },
                    is_minifigure_item: { type: 'boolean' },
                    // Tri-state: null means the composition cache has never been
                    // checked, distinct from a confirmed false (no real children).
                    has_children: { type: ['boolean', 'null'] },
                    lots: {
                        type: 'array',
                        default: []
                    },
                    lookalikes: {
                        type: 'array',
                        default: [],
                        itemSchema: { type: 'object' }
                    },
                    external_sites: {
                        type: 'array',
                        default: [],
                        itemSchema: {
                            type: 'object',
                            properties: {
                                name: { type: 'string' },
                                url: { type: 'string' }
                            }
                        }
                    }
                }
            },
            filterText: {
                type: 'string',
                default: ''
            },
            sortOption: {
                type: 'string',
                default: 'itemNumber',
                enum: ['itemNumber', 'itemName'],
                description: 'Ordering applied to identified items'
            },
            optionsSection: {
                type: 'object',
                properties: {
                    isOpen: {
                        type: 'boolean',
                        default: false
                    },
                    lookalikeChecksEnabled: {
                        type: 'boolean',
                        default: true,
                        persist: true,
                        description: 'Toggles all lookalike-related functionality for catalog searches'
                    }
                }
            },
            itemInteraction: {
                type: 'object',
                properties: {
                    hoveredItemId: {
                        type: 'string',
                        default: null
                    },
                    pressedItemId: {
                        type: 'string',
                        default: null
                    }
                }
            }
        }
    },
    itemActionComponent: {
        type: 'object',
        properties: {
            isEnabled: {
                type: 'boolean',
                default: false
            },
            isSyncingLots: {
                type: 'boolean',
                default: false
            },
            isSyncingMinifigures: {
                type: 'boolean',
                default: false
            },
            optionsSection: {
                type: 'object',
                properties: {
                    isOpen: {
                        type: 'boolean',
                        default: false
                    },
                    shouldHighlight: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    },
                    hideLowValueColors: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    },
                    showSetNeededLots: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    },
                    onlyShowSetNeededColors: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    },
                    hideExistingBatchItemLots: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    },
                    showPrices: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    },
                    automaticallyRefreshPrices: {
                        type: 'boolean',
                        persist: true,
                        default: false
                    }
                }
            },
            showLotList: {
                type: 'boolean',
                default: false
            },
            lotTable: itemActionTableSchema,
            dialogState: {
                type: 'object',
                properties: {
                    isOpen: {
                        type: 'boolean',
                        default: false
                    }
                }
            }
        }
    },
    batchCreation: {
        type: 'object',
        properties: {
            isCreating: {
                type: 'boolean',
                default: false
            },
            name: {
                type: 'string',
                default: ''
            }
        }
    },
    setAllocationTotals: {
        type: 'object',
        properties: {
            loaded: {
                type: 'boolean',
                default: false,
                description: 'True after the rolling 24h allocation totals report has loaded.'
            },
            qty: {
                type: 'number',
                default: 0,
                description: 'Pieces allocated to user sets in the rolling 24h window.'
            },
            value: {
                type: 'number',
                default: 0,
                description: 'Value allocated to user sets in the rolling 24h window.'
            },
            userSetIds: {
                type: 'array',
                default: [],
                description: 'Distinct user_set ids already counted in the window.'
            },
            lotIds: {
                type: 'array',
                default: [],
                description: 'Distinct lot ids already counted in the window.'
            }
        }
    },
    setMatchIndicator: {
        type: 'object',
        properties: {
            sortOrder: {
                type: 'string',
                enum: [
                    'alphabetical',
                    'set_value_used',
                    'set_value_new',
                    'pct_complete_desc',
                    'pct_complete_asc',
                    'value_per_piece'
                ],
                default: 'alphabetical',
                persist: true,
                description: 'Last-selected sort order for the Matching Sets popover.'
            }
        }
    },
    batchDetailsDialog: batchDetailsDialogStateSchema,
    minifigureCompletionDialog: {
        type: 'object',
        properties: {
            isOpen: {
                type: 'boolean',
                default: false
            },
            trackedInUserSets: {
                type: 'array',
                default: []
            }
        }
    },
    itemNotFoundDialog: {
        type: 'object',
        properties: {
            isOpen: {
                type: 'boolean',
                default: false
            },
            searchType: {
                type: 'string',
                enum: ['image', 'number', 'name', 'webcam'],
                default: null
            },
            searchValue: {
                type: 'string',
                default: null
            },
            itemType: {
                type: 'string',
                enum: ITEM_TYPES.filter(t => t !== 'UNSORTED_LOT'),
                default: null
            }
        }
    },
    priceRefresh: {
        type: 'object',
        properties: {
            isLoading: {
                type: 'boolean',
                default: false
            }
        }
    },
    itemSearchMethod: {
        type: 'string',
        enum: ['webcam', 'itemNumber', 'itemName'],
        default: null,
        description: 'Tracks which search method was used to find the current item for focus restoration'
    },

    lastBatchItemAdd: {
        type: ['object', 'null'],
        default: null,
        description: 'Color and quantity of the most recent successful add to the selected batch.'
    },

    lastSetItemAllocation: {
        type: ['object', 'null'],
        default: null,
        description: 'The batch-review capture that most recently received a set allocation.'
    },

    // --- Batch Mode ---
    // Batch mode replaces the three single-item panels with a capture tray and a
    // review queue. Photos are held here, in memory only, until the user confirms
    // the queue; the identify pass then fills in each capture's candidates.
    batchMode: {
        type: 'object',
        properties: {
            isEnabled: {
                type: 'boolean',
                default: false,
                persist: true,
                description: 'Whether the catalog page is showing batch mode instead of the single-item workspace.'
            },
            phase: {
                type: 'string',
                enum: BATCH_MODE_PHASES,
                default: 'capture',
                description: 'Which stage of a batch pass the workspace is showing.'
            },
            itemType: {
                type: 'string',
                enum: BATCH_MODE_ITEM_TYPES,
                default: 'PART',
                persist: true,
                description: 'Item type hint sent with every recognition request in the pass.'
            },
            currentIndex: {
                type: 'number',
                default: 0,
                description: 'Index of the capture currently under review.'
            },
            autoAdvance: {
                type: 'boolean',
                default: false,
                persist: true,
                description: 'Whether a successful add moves the review straight on to the next photo that still needs a decision.'
            },
            // Never persisted: the queue holds base64 image data for up to
            // BATCH_MODE_MAX_CAPTURES photos, far past what localStorage can take.
            captures: {
                type: 'array',
                default: [],
                description: 'Photos captured in this batch pass, in capture order.',
                itemSchema: {
                    type: 'object',
                    properties: {
                        id: { type: 'string', description: 'Client-side identity of the capture' },
                        imageData: { type: 'string', description: 'Base64 data URL of the captured frame' },
                        status: {
                            type: 'string',
                            enum: BATCH_CAPTURE_STATUSES,
                            default: 'queued'
                        },
                        candidates: {
                            type: 'array',
                            default: [],
                            description: 'Recognition candidates for this photo, best score first',
                            itemSchema: { type: 'object' }
                        },
                        selectedCandidateIndex: {
                            type: 'number',
                            default: 0,
                            description: 'Which candidate the review is currently showing'
                        },
                        // A photo can contribute several colors of the same part, so
                        // every add is appended here rather than replacing the last.
                        addedColors: {
                            type: 'array',
                            default: [],
                            description: 'Colors and quantities added to the batch for this photo',
                            itemSchema: { type: 'object' }
                        },
                        errorMessage: {
                            type: ['string', 'null'],
                            default: null,
                            description: 'Recognition failure message, when the photo could not be identified'
                        }
                    }
                }
            },
            identifyProgress: {
                type: 'object',
                properties: {
                    completed: {
                        type: 'number',
                        default: 0
                    },
                    total: {
                        type: 'number',
                        default: 0
                    }
                }
            }
        }
    }
};
